import { useSyncExternalStore } from 'react';
import type { Transition } from 'motion/react';

/**
 * Tokens de movimento. Único lugar com mola, curva e duração do app: nenhum
 * componente escreve número solto. Os valores de CSS equivalentes vivem em
 * estilo.css (--duracao-*, --ease-*) e precisam andar juntos.
 *
 * Regra de uso: entrada usa EASE_SAIDA; troca de lugar usa EASE_MOVIMENTO;
 * gesto e física usam mola; saída é sempre mais curta que a entrada.
 */
export const EASE_SAIDA = [0.16, 1, 0.3, 1] as const;
export const EASE_MOVIMENTO = [0.77, 0, 0.175, 1] as const;
export const EASE_GAVETA = [0.32, 0.72, 0, 1] as const;

export const DURACAO = {
  instante: 0.1,
  rapida: 0.16,
  base: 0.22,
  lenta: 0.36,
  cena: 0.64,
  saida: 0.12,
} as const;

/** Toque num botão: afunda rápido, volta sem quicar demais. */
export const MOLA_TOQUE: Transition = { type: 'spring', stiffness: 500, damping: 30, mass: 0.6 };
/** Indicador que desliza entre abas e itens de navegação. */
export const MOLA_TROCA: Transition = { type: 'spring', stiffness: 460, damping: 30, mass: 0.55 };
/** Diálogo, menu e aviso entrando. */
export const MOLA_PAINEL: Transition = { type: 'spring', stiffness: 420, damping: 40, mass: 0.5 };
/** Itens de lista mudando de lugar (layout). */
export const MOLA_LAYOUT: Transition = { type: 'spring', stiffness: 360, damping: 32, mass: 0.6 };
/** Reflexo que segue o cursor: macio, atrasa um pouco de propósito. */
export const MOLA_CURSOR: Transition = { type: 'spring', stiffness: 200, damping: 15, mass: 0.3 };

/** Intervalo entre itens que entram em sequência (CSS: --escalonar). */
export const ESCALONAR = 0.05;

export const ESCALA_TOQUE = { pequeno: 0.93, padrao: 0.97, largo: 0.985 } as const;

/** Transição sem deslocamento para quem pediu menos movimento. */
export const SO_OPACIDADE: Transition = { duration: DURACAO.rapida, ease: 'linear' };

/*
 * Preferência de movimento. Dois sinais valem: o do sistema
 * (prefers-reduced-motion) e o da conta (data-movimento="reduzido" no <html>,
 * aplicado antes da hidratação pelo script do index.html e mudado ao vivo
 * pelos Ajustes de leitura). Qualquer um dos dois reduz.
 */
const CONSULTA_SISTEMA = '(prefers-reduced-motion: reduce)';

function lerReduzido(): boolean {
  if (typeof window === 'undefined') return false;
  if (document.documentElement.dataset['movimento'] === 'reduzido') return true;
  return window.matchMedia?.(CONSULTA_SISTEMA).matches ?? false;
}

function assinarReduzido(avisar: () => void): () => void {
  const consulta = window.matchMedia?.(CONSULTA_SISTEMA);
  consulta?.addEventListener('change', avisar);
  const observador = new MutationObserver(avisar);
  observador.observe(document.documentElement, { attributes: true, attributeFilter: ['data-movimento'] });
  return () => {
    consulta?.removeEventListener('change', avisar);
    observador.disconnect();
  };
}

/** true quando o sistema OU a conta pedem menos movimento. Muda ao vivo. */
export function useMovimentoReduzido(): boolean {
  return useSyncExternalStore(assinarReduzido, lerReduzido, () => false);
}

const CONSULTA_PONTEIRO = '(hover: hover) and (pointer: fine)';

function lerPonteiroFino(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.(CONSULTA_PONTEIRO).matches ?? false;
}

function assinarPonteiro(avisar: () => void): () => void {
  const consulta = window.matchMedia?.(CONSULTA_PONTEIRO);
  consulta?.addEventListener('change', avisar);
  return () => consulta?.removeEventListener('change', avisar);
}

/**
 * true só com mouse ou caneta. Efeito que segue o cursor (reflexo, ímã,
 * inclinação) só liga aqui: no toque ele não tem como ser percebido.
 */
export function usePonteiroFino(): boolean {
  return useSyncExternalStore(assinarPonteiro, lerPonteiroFino, () => false);
}

/** Entrada padrão de um bloco: sobe 8px e aparece. Reduzido: só aparece. */
export function entrada(reduzido: boolean, atraso = 0): {
  initial: { opacity: number; y: number };
  animate: { opacity: number; y: number };
  transition: Transition;
} {
  return {
    initial: { opacity: 0, y: reduzido ? 0 : 8 },
    animate: { opacity: 1, y: 0 },
    transition: reduzido
      ? { ...SO_OPACIDADE, delay: 0 }
      : { duration: DURACAO.base, ease: EASE_SAIDA, delay: atraso },
  };
}
